import React, { useEffect } from "react";
import Link from "next/link";
import Head from "next/head";
import { useRouter } from "next/router";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

import BlogImage from "components/customImages/BlogImage";
import { getCurrentPath, getOrigin } from "utils/common";
import Button from "components/ui_components/button";
import { BASE_URL } from "helpers/constants/routes";

function Index() {
  const router = useRouter();
  const { locale } = router;

  useEffect(() => {
    document.documentElement.dir = "ltr";
  }, [locale]);
  const origin = getOrigin();
  const currentPath = getCurrentPath();

  return (
    <>
      <Head>
        <title>{"Les Tendances Mode de la Saison : Vêtements Femme Tunisie"}</title>
        <meta
          name="description"
          content=" Découvrez les tendances mode de la saison et les pièces incontournables à ajouter à votre garde-robe pour rester stylé et à la mode."
        />
        <link rel="alternate" hrefLang="fr" href={`${origin}${currentPath}`} />
        <link rel="alternate" hrefLang="ar" href={`${origin}/ar${currentPath}`} />
      </Head>
      <div className="flex flex-col  w-full py-10 justify-center">
        <div className="max-w-[680px] m-auto px-6">
          <h1 className="font-bold text-md lg:text-lg  mb-10 text-center ">{`Les tendances mode de la saison : ce qu’il faut absolument avoir dans sa garde-robe`}</h1>
          <time dateTime="02-04-2025">Avril 02, 2025</time>
          <p>
            {`La mode est un reflet dynamique de notre époque, et chaque saison apporte son lot de nouveautés d’habillement incontournables. Alors que les jours raccourcissent et que les températures changent, il est temps de découvrir les `}{" "}
            <span className="font-bold"> {` tendances mode `} </span>{" "}
            {` qui marqueront cette saison. Qu'il s'agisse de pièces audacieuses, de couleurs vibrantes ou de coupes innovantes, il est essentiel de savoir ce qu'il faut absolument ajouter à sa garde-robe pour rester à la pointe du style. Dans cet article, nous explorons les `}{" "}
            <span className="font-bold"> {` tendances les plus en vogue,`}</span>{" "}
            {` vous offrant un guide précieux pour rafraîchir votre look et vous démarquer avec élégance. Préparez-vous à redéfinir votre style et à faire de chaque tenue une déclaration de mode inoubliable.`}{" "}
          </p>
          <div className="flex justify-center p-5">
            <BlogImage
              imageName="image%20(1).png"
              alt="Fashion trends"
              className="object-contain"
              imageUrl="image%20(1).png"
            />
          </div>
          {/*  -------- First section ----------- */}
          <h2 className="font-bold text-md lg:text-lg mx-5">{`Les meilleures tendance vêtement femme pour cet saison `}</h2>
          <h3 className="font-bold text-md lg:text-lg mx-5">{`1. Les Robes en maille  `}</h3>

          <p>
            {`La robe en maille, qui flatte toutes les formes, est devenue, au fil des saisons, une compagne fidèle de l’été. Confortable, apaisante, pratique à porter et à personnaliser... Elle valide toutes les options.`}
          </p>
          <div className="flex justify-center p-5">
            <BlogImage
              imageName="portrait-beautiful-happy-cute-brunette-woman-girl-casual-green-hipster-summer-clothes-with-no-makeup-isolated-white-sunglasses.jpg"
              alt="Tendances mode -  Robe en maille"
              className="object-contain"
              imageUrl="portrait-beautiful-happy-cute-brunette-woman-girl-casual-green-hipster-summer-clothes-with-no-makeup-isolated-white-sunglasses.jpg"
            />
          </div>

          {/*----------- Second section ---------- */}
          <h3 className="font-bold text-md lg:text-lg m-5">{`2. La superbe touche de léopard`}</h3>
          <p>
            {`Le léopard est visible chez Jacquemus, la Belge Marie Adam-Leenaerdt et Dolce & Gabbana, et il s'intègre à notre vestiaire par petites touches ou complet. La parade idéale pour s'emparer de la tendance Mob Wife. `}{" "}
          </p>
          <div className="flex justify-center p-5">
            <BlogImage
              imageName="woman-with-coffee-bench-train-station.jpg"
              alt="La superbe touche de léopard"
              className="object-contain"
              imageUrl="woman-with-coffee-bench-train-station.jpg"
            />
          </div>
          <h3 className="font-bold text-md lg:text-lg mx-5">
            {" "}
            {`3. Des baskets à motifs rouges`}{" "}
          </h3>
          <p>{`L'éclat phare de la saison se manifeste également dans le rayon des baskets... D'après les récents rapports de Stylight, les baskets avec des motifs rouges seront les plus appréciées à l’été 2024, atteignant un taux de recherche maximal de 24% sur Google.`}</p>
          <div className="flex justify-center p-5">
            <BlogImage
              imageName="man-jeans-with-cardboard-bouquet-flowers.jpg"
              alt="Baskets Femme Tunisie"
              className="object-contain"
              imageUrl="man-jeans-with-cardboard-bouquet-flowers.jpg"
            />
          </div>

          <h3 className="mx-5"> {`4. Un bon legging`} </h3>
          <p>{`Il est surprenant que l'année 2025 soit celle du legging ! De nouveau populaire grâce à Victoria Beckham, Kylie Jenner ou Amelia Gray et présent sur de nombreux podiums du printemps-été 2025 (Balenciaga, Alaïa,...), le legging quitte les salles de sport pour se porter haut en ville.`}</p>

          <h3 className="mx-5">{`5. Une chemise de couleur vive `}</h3>
          <p>
            {`Un `} <span className="font-bold">{`vêtement femme en tunisie `}</span>{" "}
            {` à la mode très simple qui est rapidement devenu un incontournable du vestiaire féminin dès son arrivée dans les années 20 et 30 et que l'on sélectionne cette saison dans des couleurs sorbet.`}
          </p>

          <h3 className="mx-5">{`6. Des robes de style bohème`}</h3>
          <p>
            {`À l'instar du panier, la robe bohème est l'une de ces pièces associées à l’été que l'on apprécie emporter de ses placards après les jours qui s'allongent. Une tendance qui n'est pas prête à s'éteindre, à l'instar de la première collection de Chemena Kamali pour Chloé qui restaure le style "boho chic".`}
          </p>
          <div className="flex justify-center p-5">
            <BlogImage
              imageName="portrait-pretty-woman-dress-enjoying-cozy-home.jpg"
              alt="Robe de style bohème"
              className="object-contain"
              imageUrl="portrait-pretty-woman-dress-enjoying-cozy-home.jpg"
            />
          </div>
          <h3 className="mx-5">{`7. Une paire de talons compensés ou kitten heels `}</h3>
          <p>{`Étant donné leur principal avantage de ne négliger ni le confort ni le style, les chaussures kitten heels (avec leur talon mince) se positionnent aujourd'hui comme les chaussures les plus indispensables de la saison.`}</p>
          <h3 className="mx-5">{`8. Les marinières `}</h3>
          <p>{`Si la saison d’été devait être représentée par une seule pièce de mode, ce serait inévitablement la marinière. Un essentiel de style inoxydable à se procurer dans sa version la plus authentique.`}</p>

          <h3 className="mx-5">{`9. Des sandales plates `}</h3>
          <p>{`Pendant une longue période, les sandales ont maintenant démontré leur aptitude à rendre n'importe quelle tenue tendance. Grâce à leur apparence élégante sans trop en faire l'air, ce sont les choix parfaits pour compenser le confort des chaussures à talon haut.`}</p>
          <div className="flex justify-center p-5">
            <BlogImage
              imageName="city-cheerful-smile-beauty-standing-fashion.jpg"
              alt="Tendance vêtement femme"
              className="object-contain"
              imageUrl="city-cheerful-smile-beauty-standing-fashion.jpg"
            />
          </div>

          <div className="flex justify-center m-8">
            <Button>
              <Link
                target="_blank"
                rel="noopener"
                href={`${BASE_URL}categories/habillement?extraOptions=%7B%22sub_category%22%3A%7B%22name%22%3A%7B%22containsi%22%3A%22Hab+%3E+Femme%22%7D%7D%7D`}
                passHref
                legacyBehavior
              >
                {`Découvrez la nouvelle tendance de vêtements femme 2025`}
              </Link>
            </Button>
          </div>

          <h2 className="mx-5">
            {" "}
            {`Les Must-Have : Tendance mode et accessoires à ne pas manquer`}
          </h2>
          <h3 className="mx-5">{`1. Le panier`}</h3>
          <p>{`Une élégante reprise de l’été, le panier en osier ou en raphia évoque les tenues bohèmes des années 70 d'une Jane Birkin ou d'une Françoise Hardy. Un accessoire, qui est même le symbole du retour des beaux jours, que l'on apprécie toujours autant de porter au moindre rayon ensoleillé.`}</p>
          <div className="flex justify-center p-5">
            <BlogImage
              imageName="pretty-glamorous-girl-touching-her-straw-hat-studio-portrait-joyful-young-woman-with-summer-bag.jpg"
              alt="Le panier"
              className="object-contain"
              imageUrl="pretty-glamorous-girl-touching-her-straw-hat-studio-portrait-joyful-young-woman-with-summer-bag.jpg"
            />
          </div>

          <h3 className="mx-5">{`2. Beaux accessoires pour les cheveux`}</h3>
          <p>{`Les accessoires pour les cheveux comme les barrettes et les épingles ornées, les diadèmes et les bandeaux, les rubans en satin, les pinces à cheveux décoratives, les serre-têtes ornés, chapeaux élégants… sont le moyen idéal d’ajouter une touche d'élégance et de personnalité à votre coiffure. Voici quelques beaux accessoires pour cheveux qui peuvent transformer votre look en un clin d'œil `}</p>
          <h2 className="mx-5">{`Adoptez les tendances et faites de votre garde-robe une déclaration de mode`}</h2>

          <h3 className="mx-5">{`Adoptez les tendances et faites de votre garde-robe une déclaration de mode`}</h3>
          <p className="pt-2">{`Pour conclure, intégrer ces pièces incontournables de la saison dans votre garde-robe vous permettra de rester à la pointe de la mode tout en affirmant votre style unique. Ne laissez pas passer l’occasion de vous procurer ces tendances phares. `}</p>
          <p>
            {`Envie de découvrir encore plus de produits tendance et de les acheter en toute simplicité ? Rendez-vous dès maintenant sur `}{" "}
            <a
              href={BASE_URL}
              className=" underline text-blue visited:text-[#0369a1] hover:underline"
            >
              {" "}
              {`Yawakit`}
            </a>{" "}
            {` et trouvez tout ce qu’il vous faut pour compléter votre look cette saison !`}
          </p>
        </div>
      </div>
    </>
  );
}

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["layout", "categories", "signup_promotion"])),
    },
  };
}

export default Index;
